import { useState, useEffect } from "react";
import { useAppSelector } from "../Hooks/redux";
import { selectToken } from "../Redux/AuthRedux";
import Api from "../Services/Api";

const api = Api.create();

export default function SettingsPage() {
  const token = useAppSelector(selectToken);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [revealedKey, setRevealedKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) api.setAuthToken(token);
    fetchKeys();
  }, [token]);

  const fetchKeys = async () => {
    setLoading(true);
    const res = await api.getApiKeys();
    if (res.ok) setKeys(res.data.api_keys || []);
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    const res = await api.createApiKey({ name: newKeyName || "Default" });
    if (res.ok) {
      setRevealedKey(res.data.api_key);
      setNewKeyName("");
      fetchKeys();
    } else {
      setError(res.data?.errors?.join(", ") || "Failed to create API key");
    }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this API key? This cannot be undone.")) return;
    const res = await api.deleteApiKey(id);
    if (res.ok) {
      setKeys((prev) => prev.filter((k) => k.id !== id));
      if (revealedKey?.id === id) setRevealedKey(null);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Settings</h1>
        <p className="text-muted text-sm">Manage your API keys for programmatic access.</p>
      </div>

      {/* New key reveal banner */}
      {revealedKey && (
        <div className="rounded-[12px] border border-green-500/30 bg-green-500/10 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-green-300">
              API key created — copy it now, it won't be shown again.
            </p>
            <button
              onClick={() => setRevealedKey(null)}
              className="text-green-300/60 hover:text-green-300 text-lg leading-none"
            >
              &times;
            </button>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm font-mono text-green-200 bg-black/30 rounded-lg px-3 py-2 break-all">
              {revealedKey.key}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(revealedKey.key)}
              className="shrink-0 px-3 py-2 rounded-lg text-xs font-medium bg-green-500/20 text-green-300 hover:bg-green-500/30 transition-colors"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Create new key */}
      <div className="rounded-[16px] border border-muted-bg bg-card p-6 space-y-4">
        <h2 className="text-base font-semibold text-foreground">Create API Key</h2>
        <form onSubmit={handleCreate} className="flex gap-3">
          <input
            type="text"
            placeholder="Key name (optional)"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            maxLength={50}
            className="flex-1 bg-background border border-muted-bg rounded-[10px] px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:border-primary"
          />
          <button
            type="submit"
            disabled={creating}
            className="px-4 py-2 bg-primary text-white rounded-[10px] text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {creating ? "Creating..." : "Create"}
          </button>
        </form>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>

      {/* Existing keys */}
      <div className="rounded-[16px] border border-muted-bg bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-muted-bg">
          <h2 className="text-base font-semibold text-foreground">Your API Keys</h2>
        </div>

        {loading ? (
          <div className="px-6 py-8 text-center text-muted text-sm">Loading...</div>
        ) : keys.length === 0 ? (
          <div className="px-6 py-8 text-center text-muted text-sm">
            No API keys yet. Create one above to get started.
          </div>
        ) : (
          <ul className="divide-y divide-muted-bg">
            {keys.map((key) => (
              <li key={key.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-foreground truncate">{key.name || "Unnamed"}</span>
                  </div>
                  <code className="text-xs font-mono text-muted">{key.key}</code>
                  <div className="text-xs text-muted mt-0.5">
                    Created {new Date(key.created_at).toLocaleDateString()}
                    {key.last_used_at && (
                      <> · Last used {new Date(key.last_used_at).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(key.id)}
                  className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Docs link */}
      <div className="rounded-[12px] border border-muted-bg bg-card px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">API Documentation</p>
          <p className="text-xs text-muted mt-0.5">Learn how to use your API key to submit dubbing jobs.</p>
        </div>
        <a
          href="/docs"
          className="px-4 py-2 rounded-[10px] text-sm font-medium border border-muted-bg text-foreground hover:bg-secondary transition-colors"
        >
          View Docs
        </a>
      </div>
    </div>
  );
}
